import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, BookOpen } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { useContent } from '../context/ContentContext';
import FlipbookModal from '../components/FlipbookModal';
import IssueCarousel from '../components/IssueCarousel';
import { issueSlug } from '../lib/issueSlug';

function MagazineHero({ edition }) {
  const [open, setOpen] = React.useState(false);
  if (!edition) return null;
  const hasPdf = !!edition.pdfUrl;
  return (
    <section className="section-pad pt-32 md:pt-40 pb-16" data-testid="home-magazine-hero">
      <div className="max-w-[1500px] mx-auto">
        <Reveal>
          <p className="eyebrow text-[#D01919] mb-6">The Enersider Magazine · {edition.month}</p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <button onClick={() => hasPdf && setOpen(true)} className="group relative block w-full max-w-md mx-auto lg:mx-0" data-testid="home-cover-open">
              <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[3/4] ring-1 ring-black/5">
                {edition.cover ? <img src={edition.cover} alt={edition.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="w-full h-full bg-[#f4f4f4]" />}
              </div>
              {hasPdf && (
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                  <span className="inline-flex items-center gap-2 bg-white text-[#111111] font-medium px-5 py-3 rounded-full"><BookOpen size={18} /> Read the Issue</span>
                </span>
              )}
            </button>
          </Reveal>
          <Reveal delay={120}>
            <div>
              <p className="eyebrow text-[#D01919] mb-4">Current edition</p>
              <h1 className="font-display text-5xl md:text-7xl leading-[0.98]">{edition.title}</h1>
              {edition.summary ? <p className="text-[#666666] text-lg mt-6 max-w-xl leading-relaxed">{edition.summary}</p> : null}
              <div className="flex flex-wrap gap-3 mt-9">
                {hasPdf && (
                  <button onClick={() => setOpen(true)} data-testid="home-read-issue" className="inline-flex items-center gap-2 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors">
                    <BookOpen size={18} /> Read the Issue
                  </button>
                )}
                <Link to="/editions" className="inline-flex items-center gap-1.5 border border-[#111111]/20 text-[#111111] font-medium px-6 py-3.5 rounded-full hover:bg-[#f4f4f4] transition-colors">
                  Browse all editions <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
      {hasPdf && <FlipbookModal open={open} onClose={() => setOpen(false)} url={edition.pdfUrl} title={`${edition.title} · ${edition.month}`} />}
    </section>
  );
}

function NewsWire({ wire }) {
  if (!wire || wire.length === 0) return null;
  return (
    <section className="section-pad py-6 border-b border-[#e5e5e5]">
      <div className="max-w-[1500px] mx-auto flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="flex items-center gap-2.5 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-[#D01919] opacity-70" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D01919]" />
          </span>
          <span className="font-grotesk text-xs uppercase tracking-[0.18em] font-semibold">The Wire</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap md:flex-nowrap md:overflow-x-auto gap-x-8 gap-y-2 no-scrollbar">
            {wire.slice(0, 6).map((n) => (
              <a key={n.id} href={n.url || '#'} target={n.url && n.url !== '#' ? '_blank' : undefined} rel="noopener noreferrer" className="group flex items-center gap-2 shrink-0 md:whitespace-nowrap">
                <span className="text-[0.62rem] font-bold uppercase tracking-wider text-[#D01919] border border-[#D01919]/40 rounded px-1.5 py-0.5">{n.source}</span>
                <span className="text-sm text-[#111111]/80 group-hover:text-[#111111] transition-colors">{n.headline}</span>
              </a>
            ))}
          </div>
        </div>
        <span className="hidden lg:block shrink-0 text-[0.68rem] text-[#666666]">Syndicated · ANI · PTI · PIB</span>
      </div>
    </section>
  );
}

function Ticker() {
  const items = ['Green Hydrogen', 'Firm Power', 'Long-Duration Storage', 'Carbon Markets', 'Offshore Wind', 'LNG Economics', 'Grid Software', 'EV Charging'];
  const doubled = [...items, ...items];
  return (
    <div className="bg-[#111111] text-white py-4 overflow-hidden">
      <div className="flex whitespace-nowrap marquee-track">
        {doubled.map((it, i) => (
          <span key={i} className="flex items-center gap-4 px-6 font-grotesk text-sm uppercase tracking-widest">{it} <span className="text-[#D01919]">✦</span></span>
        ))}
      </div>
    </div>
  );
}

function LatestIssue({ edition, digitalEditionUrl }) {
  if (!edition) return null;
  const readUrl = edition.readUrl || digitalEditionUrl;
  return (
    <section className="section-pad py-20 bg-[#111111] text-white">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[3/4]">
              <img src={edition.cover} alt={edition.title} className="w-full h-full object-cover" />
            </div>
            <span className="absolute -bottom-4 -right-2 md:right-8 bg-[#D01919] text-white rounded-2xl px-5 py-3 shadow-xl">
              <span className="block font-grotesk text-[0.62rem] uppercase tracking-widest">Latest Issue</span>
              <span className="font-display text-lg">{edition.month}</span>
            </span>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div>
            <p className="eyebrow text-[#D01919] mb-4">Hot off the press · {edition.month}</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98]">{edition.title}</h2>
            <p className="text-white/70 text-lg mt-6 max-w-lg">{edition.summary}</p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/editions" className="inline-flex items-center gap-2 bg-white text-[#111111] font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] hover:text-white transition-colors">
                <BookOpen size={18} /> Read this issue
              </Link>
              <Link to="/editions" className="inline-flex items-center gap-1.5 border border-white/30 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                Browse all editions <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TopicsSection({ topics }) {
  return (
    <section className="section-pad py-20 bg-[#f4f4f4]">
      <div className="max-w-[1500px] mx-auto">
        <div className="mb-10">
          <p className="eyebrow text-[#D01919] mb-3">Beats we cover</p>
          <h2 className="font-display text-4xl md:text-5xl">Follow the currents</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.map((t, i) => (
            <Reveal key={t.slug} delay={i * 50}>
              <Link to="/magazine" className="group flex items-center justify-between bg-white rounded-2xl p-6 hover:bg-[#111111] transition-colors duration-400 h-full">
                <div>
                  <h3 className="font-display text-2xl group-hover:text-white transition-colors">{t.name}</h3>
                  <p className="text-[#666666] text-sm mt-1 group-hover:text-white/70 transition-colors">{t.blurb}</p>
                </div>
                <span className="w-10 h-10 rounded-full border border-[#e5e5e5] flex items-center justify-center shrink-0 ml-4 group-hover:bg-[#D01919] group-hover:border-[#D01919] transition-colors">
                  <ArrowUpRight size={18} className="text-[#D01919] group-hover:text-white" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection({ stats }) {
  return (
    <section className="section-pad py-20 bg-[#111111] text-white">
      <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="border-l border-white/20 pl-6">
              <p className="font-display text-5xl md:text-6xl text-[#D01919]">{s.value}</p>
              <p className="text-white/70 mt-2 text-sm">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EventsPreview({ events }) {
  return (
    <section className="section-pad py-24">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow text-[#D01919] mb-3">Dialogues & summits</p>
            <h2 className="font-display text-4xl md:text-5xl">Where the sector convenes</h2>
          </div>
          <Link to="/events" className="hidden sm:inline-flex items-center gap-1.5 font-medium text-[#111111] link-underline">All events <ArrowRight size={17} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {events.map((e, i) => (
            <Reveal key={e.id || e.title} delay={i * 80}>
              <Link to="/events" className="group block rounded-2xl overflow-hidden bg-[#f4f4f4] h-full">
                <div className="aspect-[16/10] overflow-hidden"><img src={e.image} alt={e.title} className="card-img w-full h-full object-cover" /></div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-[#666666]"><span className="bg-[#111111] text-[#D01919] px-2.5 py-1 rounded-full font-medium">{e.partner ? 'Partner Event' : 'Event'}</span><span>{e.date}</span></div>
                  <h3 className="font-display text-2xl mt-3 group-hover:text-[#D01919] transition-colors">{e.title}</h3>
                  <p className="text-[#666666] text-sm mt-2">{e.location}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClubBanner({ clubImage }) {
  return (
    <section className="section-pad pb-24">
      <div className="max-w-[1500px] mx-auto">
        <div className="relative rounded-3xl overflow-hidden">
          <img src={clubImage} alt="One Enersider Club" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#111111]/70" />
          <div className="relative p-10 md:p-16 max-w-2xl text-white">
            <p className="eyebrow text-[#D01919] mb-4">One Enersider Club</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">A room of decision-makers, once a month.</h2>
            <p className="text-white/75 text-lg mt-5">An invitation-only forum where energy leaders, policymakers and change-makers meet for candid, high-level dialogue.</p>
            <Link to="/club" className="inline-flex items-center gap-1.5 mt-8 bg-[#D01919] text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white hover:text-[#111111] transition-colors">Request an invitation <ArrowUpRight size={18} /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnersMarquee({ partners }) {
  const doubled = [...partners, ...partners];
  return (
    <section className="py-14 border-y border-[#e5e5e5] overflow-hidden">
      <p className="eyebrow text-center text-[#666666] mb-8">In partnership with</p>
      <div className="flex whitespace-nowrap marquee-track">
        {doubled.map((p, i) => (<span key={i} className="font-display text-2xl md:text-3xl text-[#111111]/40 px-10">{p}</span>))}
      </div>
    </section>
  );
}

const SECTORS_FALLBACK = ['Oil & Gas', 'Power', 'GH2', 'RE (Solar + Wind)', 'Bio-Energy', 'BESS', 'Thermal', 'Hydro', 'Nuclear', 'Energy×Water', 'Energy×Infra'];

function SectorDesk({ wire, sectors }) {
  const list = (sectors && sectors.length) ? sectors : SECTORS_FALLBACK;
  const [active, setActive] = React.useState(list[0]);
  const inSector = (n) => (Array.isArray(n.sectors) && n.sectors.includes(active)) || (n.sector || '') === active;
  const feed = wire.filter(inSector).slice(0, 6);
  return (
    <section className="section-pad py-20 border-t border-[#e5e5e5]">
      <div className="max-w-[1500px] mx-auto">
        <div className="mb-8">
          <p className="eyebrow text-[#D01919] mb-3">Sector Desk</p>
          <h2 className="font-display text-4xl md:text-5xl">The month, by sector</h2>
          <p className="text-[#666666] mt-2 text-sm max-w-xl">The month's top approved stories across India's energy sectors.</p>
        </div>
        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          <div className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar border-r-0 lg:border-r border-[#e5e5e5] pr-0 lg:pr-4">
            {list.map((s) => (
              <button key={s} onClick={() => setActive(s)} className={`text-left whitespace-nowrap px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shrink-0 ${active === s ? 'bg-[#111111] text-white' : 'text-[#111111]/70 hover:bg-[#f4f4f4]'}`}>{s}</button>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">{active}</h3>
              <span className="text-xs text-[#666666]">Top {Math.min(6, feed.length)} this month</span>
            </div>
            <div className="divide-y divide-[#e5e5e5]">
              {feed.map((n, i) => (
                <a key={n.id || i} href={n.url || '#'} target={n.url && n.url !== '#' ? '_blank' : undefined} rel="noopener noreferrer" className="group flex items-start gap-4 py-4">
                  <span className="font-display text-xl text-[#e5e5e5] w-7 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <span className="text-[0.6rem] font-bold uppercase tracking-wider text-[#D01919]">{n.source}</span>
                    <p className="text-[#111111] group-hover:text-[#D01919] transition-colors">{n.headline}</p>
                  </div>
                </a>
              ))}
              {feed.length === 0 && <p className="py-6 text-sm text-[#666666]">Approve stories in the admin to populate this sector.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EditorialStrip() {
  return (
    <section className="section-pad py-10">
      <div className="max-w-[1500px] mx-auto bg-[#f4f4f4] rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-[#111111]"><span className="font-semibold">Editorial opportunities & story pitches</span> — write to us at <a href="mailto:editor@enersider.com" className="text-[#D01919] font-medium">editor@enersider.com</a></p>
      </div>
    </section>
  );
}

function CoversGallery({ editions }) {
  if (!editions || editions.length === 0) return null;
  return (
    <section className="section-pad py-20 bg-[#111111] text-white">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow text-[#D01919] mb-3">Every cover tells a story</p>
            <h2 className="font-display text-4xl md:text-5xl">The covers</h2>
          </div>
          <Link to="/editions" className="inline-flex items-center gap-1.5 font-medium text-white/80 hover:text-white link-underline">All editions <ArrowRight size={17} /></Link>
        </div>
        <IssueCarousel items={editions} dark testid="home-covers" slide="basis-[42%] sm:basis-[28%] lg:basis-[15.5%]" renderItem={(e) => (
          <Link to={`/magazine/${issueSlug(e)}`} className="group block" draggable={false} data-testid={`home-cover-${issueSlug(e)}`}>
            <div className="rounded-xl overflow-hidden aspect-[3/4] shadow-lg ring-1 ring-white/10">
              <img src={e.cover} alt={e.month} draggable={false} className="card-img w-full h-full object-cover" />
            </div>
            <p className="text-sm text-white/70 mt-2 group-hover:text-white transition-colors">{e.month}</p>
          </Link>
        )} />
      </div>
    </section>
  );
}

export default function Home() {
  const { topics, stats, partners, eventsData, clubImage, wire, editions, sectors } = useContent();
  const latestEdition = editions.find((e) => e.latest) || editions[0];
  return (
    <div>
      <MagazineHero edition={latestEdition} />
      <NewsWire wire={wire} />
      <Ticker />
      <SectorDesk wire={wire} sectors={sectors} />
      <CoversGallery editions={editions} />
      <TopicsSection topics={topics} />
      <StatsSection stats={stats} />
      <EventsPreview events={eventsData.partners} />
      <ClubBanner clubImage={clubImage} />
      <EditorialStrip />
      <PartnersMarquee partners={partners} />
    </div>
  );
}
